import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import SubHeader from 'components/common/SubHeader';
import StarRating from 'components/review/StarRating';
import MultiImageUpload from 'components/common/MultiImageUpload';
import { useSnackbar } from 'contexts/SnackbarContext';
import { createReview, getReviewDetail, updateReview } from 'api/review';
import { reviewSchema } from 'utils/zodSchema/review';
import 'assets/scss/pages/Review.scss';

const ReviewWrite = () => {
    const { reviewId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSuccess, showError } = useSnackbar();
    const isEditMode = !!reviewId;

    const stateInfo = location.state || {};
    const [productInfo, setProductInfo] = useState(
        stateInfo.productName ? { name: stateInfo.productName, thumbnail: stateInfo.productImage } : null
    );
    const [initialImages, setInitialImages] = useState([]);
    const [loading, setLoading] = useState(isEditMode);
    const [submitting, setSubmitting] = useState(false);

    const newImagesRef = useRef([]);
    const existingImagesRef = useRef([]);

    const {
        register,
        handleSubmit,
        control,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(reviewSchema),
        defaultValues: {
            rating: 0,
            content: '',
        },
    });

    const contentValue = watch('content', '');

    useEffect(() => {
        if (!isEditMode) return;
        setLoading(true);
        getReviewDetail(reviewId)
            .then((data) => {
                setProductInfo(data.product_info || null);
                const existingUrls = (data.images || []).map((img) => img.image);
                setInitialImages(existingUrls);
                existingImagesRef.current = existingUrls;
                reset({
                    rating: data.rating,
                    content: data.content,
                });
            })
            .catch(() => showError('리뷰 정보를 불러오지 못했습니다'))
            .finally(() => setLoading(false));
    }, [isEditMode, reviewId, reset, showError]);

    const handleImagesUploaded = (newFiles, existingUrls) => {
        newImagesRef.current = newFiles;
        existingImagesRef.current = existingUrls;
    };

    const onSubmit = async (formData) => {
        if (formData.rating === 0) {
            showError('별점을 선택해주세요');
            return;
        }
        setSubmitting(true);
        try {
            if (isEditMode) {
                await updateReview(reviewId, {
                    rating: formData.rating,
                    content: formData.content,
                    images: newImagesRef.current,
                    existing_images: existingImagesRef.current,
                });
                showSuccess('리뷰가 수정되었습니다');
            } else {
                await createReview({
                    product_id: stateInfo.productId,
                    product_type: stateInfo.productType || 'products',
                    rating: formData.rating,
                    content: formData.content,
                    images: newImagesRef.current,
                });
                showSuccess('리뷰가 등록되었습니다');
            }
            navigate(-1);
        } catch {
            showError(isEditMode ? '리뷰 수정에 실패했습니다' : '리뷰 등록에 실패했습니다');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="page-review-write">
                <SubHeader title={isEditMode ? '리뷰 수정' : '리뷰 작성'} />
                <div className="page-review-write__loading">불러오는 중...</div>
            </div>
        );
    }

    return (
        <div className="page-review-write">
            <SubHeader title={isEditMode ? '리뷰 수정' : '리뷰 작성'} />

            <div className="page-review-write__body">
                {productInfo && (
                    <div className="page-review-write__product">
                        {productInfo.thumbnail && (
                            <img
                                src={productInfo.thumbnail}
                                alt={productInfo.name}
                                className="page-review-write__product-thumb"
                            />
                        )}
                        <span className="page-review-write__product-name">{productInfo.name}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <section className="page-review-write__section">
                        <label className="page-review-write__label">
                            별점 <span className="page-review-write__required">*</span>
                        </label>
                        <Controller
                            name="rating"
                            control={control}
                            render={({ field }) => (
                                <StarRating
                                    rating={field.value}
                                    onChange={(val) => field.onChange(val)}
                                    size={36}
                                />
                            )}
                        />
                        {errors.rating && (
                            <p className="page-review-write__error">{errors.rating.message}</p>
                        )}
                    </section>

                    <section className="page-review-write__section">
                        <label className="page-review-write__label" htmlFor="review-content">
                            리뷰 내용 <span className="page-review-write__required">*</span>
                        </label>
                        <div className="page-review-write__textarea-wrap">
                            <textarea
                                id="review-content"
                                className={`page-review-write__textarea ${errors.content ? 'page-review-write__textarea--error' : ''}`}
                                placeholder="솔직한 리뷰를 남겨주세요. (최소 10자)"
                                maxLength={500}
                                {...register('content')}
                            />
                            <span className="page-review-write__char-count">
                                {contentValue.length} / 500
                            </span>
                        </div>
                        {errors.content && (
                            <p className="page-review-write__error">{errors.content.message}</p>
                        )}
                    </section>

                    <section className="page-review-write__section">
                        <label className="page-review-write__label">
                            사진 첨부 <span className="page-review-write__optional">(최대 5장)</span>
                        </label>
                        <MultiImageUpload
                            onImagesUploaded={handleImagesUploaded}
                            initialImages={initialImages}
                            maxImages={5}
                        />
                    </section>

                    <div className="page-review-write__submit-wrap">
                        <button
                            type="submit"
                            className="page-review-write__submit-btn"
                            disabled={submitting}
                        >
                            {submitting ? '처리 중...' : isEditMode ? '수정 완료' : '등록하기'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReviewWrite;
